import React, {useEffect, useState} from "react";
import Container from "../../../../components/Container";
import Flex, {FlexItem} from "../../../../components/Flex";
import Tag from "../../../../components/Tag";
import Title from "../../../../components/Title";
import {Size} from "../../../../props/common";
import io, {Socket} from "socket.io-client";
import {DefaultEventsMap} from "@socket.io/component-emitter";
import env from "../../../../config/env";
import {
    _sortMatchesByTableNumber,
    emptyMatch,
    MatchProps,
    PlayerProps,
    teamEmptyMatch,
    TeamMatchProps
} from "../../matchProps";
import {fetchAPI} from "../../../../config/fetch";
import Spacer from "../../../../components/Spacer";
import {green} from "../../../../config/style";
import Divider from "../../../../components/Divider";
import ftcissy from "../../../../assets/logos/FTC issy Rond.png";
import {getBestMatchingPlayer} from "../../../../utils/getBestMatchingPlayer";
import {cleanName} from "../../../../utils/cleanName";

let socket: Socket<DefaultEventsMap, DefaultEventsMap> = io(env.SOCKET, {transports: ["websocket"]});

const _computeSubstitutes = (match: TeamMatchProps, team: "one" | "two"): PlayerProps[] => {
    const substitutes: PlayerProps[] = [];
    if (!match.all_single_matches.find((singleMatch: MatchProps) => singleMatch.player_one.fullname === '--' || singleMatch.player_two.fullname === '--')) {
        const playersPlaying: PlayerProps[] = match.all_single_matches.map((singleMatch: MatchProps) => singleMatch[`player_${team}`]);
        match[`team_${team}`].all_players.forEach((player: PlayerProps) => {
            if (!playersPlaying.some((playingPlayer: PlayerProps) => cleanName(`${playingPlayer.firstname} ${playingPlayer.lastname}`) === cleanName(`${player.firstname} ${player.lastname}`))) {
                substitutes.push(player);
            }
        });
    }
    while (substitutes.length < 2) {
        substitutes.push({...emptyMatch.player_one, firstname: '-', lastname: '-'});
    }
    return substitutes.slice(0, 2);
};

const _computeSingleMatchGoals = (allSingleMatches: MatchProps[]) => {
    let teamOneGoals = 0;
    let teamTwoGoals = 0;

    allSingleMatches.forEach((singleMatch: MatchProps) => {
        teamOneGoals += singleMatch.score_player_one;
        teamTwoGoals += singleMatch.score_player_two;
    });

    return {teamOneGoals, teamTwoGoals};
};

const _playerLastnameUppercase = (playerName: string): string => {
    const playerNameSplitted: string[] = playerName.split(" ");
    const playerLastnameUppercased = playerNameSplitted[playerNameSplitted.length - 1].toUpperCase();
    playerNameSplitted.pop();
    return `${playerNameSplitted.join(" ")} ${playerLastnameUppercased}`;
};

const _generateOneSubstituteTag = (subOne: PlayerProps, subTwo: PlayerProps) => {
    return <Flex alignItems="flex-start" key={`sub-${Math.random()}`}>
        <FlexItem width="5vw"/>
        <FlexItem width="42.5vw">
            <Tag
                fontSize={Size.LEVEL_FOUR} backgroundFlag={subOne?.country}
                clubLogo={subOne?.club}
                small star={subOne?.star}
            >
                {_playerLastnameUppercase(`${subOne.firstname} ${subOne.lastname}`)}
            </Tag>
        </FlexItem>
        <FlexItem width="5vw"/>
        <FlexItem width="5vw"/>
        <FlexItem width="42.5vw">
            <Tag
                fontSize={Size.LEVEL_FOUR} backgroundFlag={subTwo?.country}
                clubLogo={subTwo?.club}
                small clubLogoPosition="right" star={subTwo?.star}
            >
                {_playerLastnameUppercase(`${subTwo.firstname} ${subTwo.lastname}`)}
            </Tag>
        </FlexItem>
    </Flex>;
};

const _generateOnePlayerTag = (match: TeamMatchProps, index: number) => {
    const singleMatch: MatchProps = match.all_single_matches[index];
    const playerOne: string = getBestMatchingPlayer(singleMatch.player_one.fullname, singleMatch.substitute_player_one);
    const playerTwo: string = getBestMatchingPlayer(singleMatch.player_two.fullname, singleMatch.substitute_player_two);

    return <Flex alignItems="flex-start" key={match.id + Math.random()}>
        <FlexItem width="5vw">
            <Tag fontSize={Size.LEVEL_THREE}>
                {singleMatch.table_number}
            </Tag>
        </FlexItem>
        <FlexItem width="42.5vw">
            <Tag
                fontSize={Size.LEVEL_FOUR} backgroundFlag={singleMatch.player_one?.country}
                clubLogo={singleMatch.player_one?.club}
                small star={singleMatch.player_one?.star}
            >
                {_playerLastnameUppercase(playerOne)}
            </Tag>
        </FlexItem>
        <FlexItem width="5vw">
            <Tag
                fontSize={Size.LEVEL_FOUR}
                borderColor={singleMatch.score_player_one > singleMatch.score_player_two ? green : "transparent"}
            >
                {singleMatch.score_player_one}
            </Tag>
        </FlexItem>
        <FlexItem width="5vw">
            <Tag
                fontSize={Size.LEVEL_FOUR}
                borderColor={singleMatch.score_player_one < singleMatch.score_player_two ? green : "transparent"}
            >
                {singleMatch.score_player_two}
            </Tag>
        </FlexItem>
        <FlexItem width="42.5vw">
            <Tag
                fontSize={Size.LEVEL_FOUR} backgroundFlag={singleMatch.player_two?.country}
                clubLogo={singleMatch.player_two?.club}
                small clubLogoPosition="right" star={singleMatch.player_two?.star}
            >
                {_playerLastnameUppercase(playerTwo)}
            </Tag>
        </FlexItem>
    </Flex>;
};

const _generateSubstituteTags = (match: TeamMatchProps): React.ReactElement[] => {
    const substitutesTeamOne = _computeSubstitutes(match, "one");
    const substitutesTeamTwo = _computeSubstitutes(match, "two");
    let substituteTags: React.ReactElement[] = [];
    for (let i = 0; i < 2; i++) {
        substituteTags.push(_generateOneSubstituteTag(substitutesTeamOne[i], substitutesTeamTwo[i]));
    }
    return substituteTags;
};

const _generatePlayerTags = (match: TeamMatchProps) => {
    let playerTags: React.ReactElement[] = [];
    for (let i = 0; i < 4; i++) {
        if (match.all_single_matches[i]) {
            playerTags.push(_generateOnePlayerTag(match, i));
        }
    }
    return playerTags;
};

const ViewSingleTeamMatch = ({queryParams}: any) => {
    const [match, setMatch] = useState<TeamMatchProps>(teamEmptyMatch);
    const [allSingleMatchGoals, setAllSingleMatchGoals] = useState({teamOneGoals: 0, teamTwoGoals: 0});
    const [readyToUpdate, setReadyToUpdate] = useState<boolean>(true);

    useEffect(() => {
        socket.on("updated-scores", (readyToUpdate, callback) => {
            setReadyToUpdate(readyToUpdate);
        });

        socket.on("updated-current-round", (readyToUpdate, callback) => {
            setReadyToUpdate(readyToUpdate);
        });
    }, []);

    useEffect(() => {
        fetchAPI(`/matches/CdF2025/team/rounds`)
            .then(matches => {
                if (Object.keys(queryParams).length && Object.keys(matches).length) {
                    let reducedMatch: TeamMatchProps = matches.filter((match: TeamMatchProps) => match.table_number >= queryParams.from && match.table_number <= queryParams.to)[0];
                    if (reducedMatch && reducedMatch.all_single_matches) {
                        reducedMatch.all_single_matches = _sortMatchesByTableNumber(reducedMatch.all_single_matches);
                        setMatch(reducedMatch);
                    }
                } else {
                    setMatch(teamEmptyMatch);
                }
                setReadyToUpdate(false);
            });
    }, [readyToUpdate, queryParams]);

    useEffect(() => {
        setAllSingleMatchGoals(_computeSingleMatchGoals(match.all_single_matches));
    }, [match]);

    const computeNameLevel = (name: string): Size => {
        if (name.length > 25) {
            return Size.LEVEL_FOUR;
        } else if (name.length > 16) {
            return Size.LEVEL_FIVE;
        } else {
            return Size.LEVEL_SIX;
        }
    };

    return (
        <Flex>
            <Container padding={Size.LEVEL_ZERO}>
                {
                    match && match.id >= 0 ?
                        <>
                            <Flex alignItems="flex-start">
                                <FlexItem width="5vw">
                                    <Flex justifyContent="center">
                                        <img src={ftcissy} width="80px" alt="Logo Issy"/>
                                    </Flex>
                                </FlexItem>
                                <FlexItem width="42.5vw">
                                    <Tag
                                        fontSize={computeNameLevel(match.team_one.name)}
                                        backgroundFlag={match.team_one.country}
                                        small
                                    >
                                        {match.team_one.name}
                                    </Tag>
                                </FlexItem>
                                <FlexItem width="5vw">
                                    <Tag fontSize={Size.LEVEL_SIX}
                                         borderColor={match.score_team_one > match.score_team_two ? green : (allSingleMatchGoals.teamOneGoals > allSingleMatchGoals.teamTwoGoals) ? green : "transparent"}>
                                        {match.score_team_one}
                                    </Tag>
                                    <Spacer size={Size.LEVEL_TWO}/>
                                    <p style={{textAlign: "center", fontSize: "20px", fontWeight: "bold"}}>
                                        {allSingleMatchGoals.teamOneGoals}
                                    </p>
                                </FlexItem>
                                <FlexItem width="5vw">
                                    <Tag fontSize={Size.LEVEL_SIX}
                                         borderColor={match.score_team_one < match.score_team_two ? green : (allSingleMatchGoals.teamOneGoals < allSingleMatchGoals.teamTwoGoals) ? green : "transparent"}>
                                        {match.score_team_two}
                                    </Tag>
                                    <Spacer size={Size.LEVEL_TWO}/>
                                    <p style={{textAlign: "center", fontSize: "20px", fontWeight: "bold"}}>
                                        {allSingleMatchGoals.teamTwoGoals}
                                    </p>
                                </FlexItem>
                                <FlexItem width="42.5vw">
                                    <Tag
                                        fontSize={computeNameLevel(match.team_two.name)}
                                        backgroundFlag={match.team_two.country}
                                        small
                                    >
                                        {match.team_two.name}
                                    </Tag>
                                </FlexItem>
                            </Flex>

                            <Spacer size={Size.LEVEL_THREE}/>

                            {_generatePlayerTags(match)}

                            <Spacer size={Size.LEVEL_TWO}/>
                            <Flex>
                                <FlexItem width="5vw"/>
                                <FlexItem width="42.5vw">
                                    <Flex justifyContent="center">
                                        <Divider
                                            direction="horizontal" color={green} size="50%"
                                            margin={Size.LEVEL_FIVE}
                                        />
                                    </Flex>
                                </FlexItem>
                                <FlexItem width="5vw"/>
                                <FlexItem width="5vw"/>
                                <FlexItem width="42.5vw">
                                    <Flex justifyContent="center">
                                        <Divider
                                            direction="horizontal" color={green} size="50%"
                                            margin={Size.LEVEL_FIVE}
                                        />
                                    </Flex>
                                </FlexItem>
                            </Flex>

                            {_generateSubstituteTags(match)}
                        </>
                        : <Title>No match found</Title>
                }
            </Container>
        </Flex>
    );
};

export default ViewSingleTeamMatch;
